/**
 * UseDataSourceSelections — app-data wiring for the pure selection helpers in
 * `../dataSourceSelections.ts`. Owns the catalog (flags + custom sources), the
 * id→name lookups for chip labels, and the same auto-sync effects the legacy
 * `DataSources.js` panel ran (current SITREP / incident / exercise).
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  useHighSecurityFlag,
  useNeoFlag,
  useTennesseeNationalGuardFlag,
} from 'utils/featureFlags';
import {
  selectAllExerciseWorkspaces,
  selectExerciseWorkspace,
  selectGroup,
  useAppSelector,
} from 'slices/commonSelectors';
import { useCapacityAnalysisList } from 'hooks/useCapacity';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';

import { useSitrepsV2 } from '../../hooks/useSitrepsV2';
import { useCustomDataSources } from '../../hooks/useCustomDataSources';
import { generateInferredName } from '../../generateInferredName';
import { useAiWorkspaceIncident } from '../useAiWorkspaceIncident';
import type { SelectedLayerForAI } from '../../utils/prefetchLayerFeaturesForAI';
import {
  buildAvailableSources,
  displayNameFor,
  filterSources,
  getLayersFilterByDatetime,
  getSeeristRequest,
  getSelectedFileIds,
  getSelectedLayers,
  isSourceSelected,
  needsConfiguration,
  selectionDisplayCount,
  setLayersFilterByDatetime,
  getWebDateRange,
  setSeeristRequest,
  setSelectedFiles,
  setSelectedLayers,
  setSourceId,
  setWebDateRange,
  syncSitrepSelection,
  syncWorkspaceSelection,
  toggleSource,
  type ConfigTab,
  type CustomDataSourceRow,
  type DataSourceOption,
  type DisplayNameLookups,
  type WebDateRange,
} from '../dataSourceSelections';
import type { RealtimeSelection } from '../types';

export interface SelectOption {
  value: string;
  label: string;
}

export interface UseDataSourceSelectionsOptions {
  selections: RealtimeSelection[];
  setSelections: React.Dispatch<React.SetStateAction<RealtimeSelection[]>>;
  usingSitrep?: boolean;
  searchTerm?: string;
}

export interface DataSourceSelections {
  /** All sources for the current flags (unfiltered). */
  allSources: DataSourceOption[];
  standardSources: DataSourceOption[];
  customSources: DataSourceOption[];
  selectedSources: DataSourceOption[];
  isSelected: (source: string) => boolean;
  needsConfig: (source: DataSourceOption) => boolean;
  displayName: (source: DataSourceOption | RealtimeSelection) => string;
  count: number;

  /** Toggle a source; returns the config tab that should open (if any). */
  toggle: (source: DataSourceOption) => ConfigTab;
  remove: (source: string) => void;
  setId: (source: string, id: string | null) => void;

  fileIds: string[];
  setFileIds: (ids: string[]) => void;
  layers: SelectedLayerForAI[];
  setLayers: (layers: SelectedLayerForAI[]) => void;
  layersFilterByDatetime: boolean;
  setLayersFilterByDatetimeEnabled: (enabled: boolean) => void;
  seeristRequest: Record<string, unknown>;
  setSeerist: (request: Record<string, unknown>) => void;
  webDateRange: WebDateRange | null;
  setWebDateRange: (range: WebDateRange | null) => void;

  sitrepOptions: SelectOption[];
  neoOptions: SelectOption[];
  incidentOptions: SelectOption[];
  exerciseOptions: SelectOption[];
  /** Workspace used for the file browser (incident → exercise → org). */
  workspaceId: string;
}

const toMap = (options: SelectOption[]): Record<string, string> =>
  Object.fromEntries(options.map((o) => [o.value, o.label]));

export const useDataSourceSelections = ({
  selections,
  setSelections,
  usingSitrep = false,
  searchTerm = '',
}: UseDataSourceSelectionsOptions): DataSourceSelections => {
  const safeSelections = useMemo(
    () => (Array.isArray(selections) ? selections : []),
    [selections]
  );

  const neo = useNeoFlag();
  const highSecurity = useHighSecurityFlag();
  const tennesseeNationalGuard = useTennesseeNationalGuardFlag();
  const isUsingTeams =
    typeof sessionStorage !== 'undefined' &&
    (sessionStorage['isUsingTeams'] === 'true' ||
      (sessionStorage['isUsingTeams'] as unknown) === true);

  const { data: customDataSources = [] } = useCustomDataSources() as {
    data?: CustomDataSourceRow[];
  };
  const { data: sitreps = [] } = useSitrepsV2() as {
    data?: { id: string; name: string; archived?: boolean }[];
  };
  const currentSitrepId = useSelector(
    (s: { dchat: { current_sitrep_id?: string | null } }) =>
      s.dchat.current_sitrep_id
  );
  const group = useAppSelector(selectGroup) as { group_guid: string };
  const { data: capacityAnalysisList = [] } = useCapacityAnalysisList(
    group.group_guid as never
  ) as { data?: Record<string, unknown>[] };
  const currentIncident = useAiWorkspaceIncident();
  const { data: allIncidents = [] } = useAllIncidents() as {
    data?: { id: string; name: string; archived?: boolean }[];
  };
  const currentExercise = useAppSelector(selectExerciseWorkspace) as
    | { id: string }
    | undefined;
  const rawExercises = useAppSelector(selectAllExerciseWorkspaces) as
    | { id: string; displayName: string; status?: string }[]
    | undefined;
  const allExercises = useMemo(() => rawExercises ?? [], [rawExercises]);
  const mapCtx = useOptionalUscgMapContext();

  const allSources = useMemo(
    () =>
      buildAvailableSources({
        usingSitrep,
        isUsingTeams,
        highSecurity,
        tennesseeNationalGuard,
        neo,
        customDataSources,
      }),
    [
      usingSitrep,
      isUsingTeams,
      highSecurity,
      tennesseeNationalGuard,
      neo,
      customDataSources,
    ]
  );

  const filtered = useMemo(
    () => filterSources(allSources, searchTerm),
    [allSources, searchTerm]
  );

  const sitrepOptions = useMemo<SelectOption[]>(
    () =>
      sitreps
        .filter((s) => !s.archived)
        .map((s) => ({ value: s.id, label: s.name })),
    [sitreps]
  );
  const neoOptions = useMemo<SelectOption[]>(
    () =>
      capacityAnalysisList.map((n) => ({
        value: String(n.id),
        label: generateInferredName(n as never),
      })),
    [capacityAnalysisList]
  );
  const incidentOptions = useMemo<SelectOption[]>(
    () =>
      allIncidents
        .filter((i) => !i.archived)
        .map((i) => ({ value: i.id, label: i.name })),
    [allIncidents]
  );
  const exerciseOptions = useMemo<SelectOption[]>(
    () =>
      allExercises
        .filter((e) => e.status === 'Available')
        .map((e) => ({ value: e.id, label: e.displayName })),
    [allExercises]
  );

  const lookups = useMemo<DisplayNameLookups>(() => {
    const layerTimeLabelById: Record<string, string | undefined> = {};
    Object.entries(mapCtx?.externalLayerTimeFilterById ?? {}).forEach(
      ([id, f]) => {
        layerTimeLabelById[id] = (f as { label?: string } | undefined)?.label;
      }
    );
    const layerLegendLabelById: Record<string, string | undefined> = {};
    Object.entries(mapCtx?.legendClassFilterByLayerId ?? {}).forEach(
      ([id, legend]) => {
        const l = legend as
          | { labels?: string[]; values?: unknown[] }
          | undefined;
        if (!l?.labels?.length && !l?.values?.length) return;
        layerLegendLabelById[id] = l.labels?.length
          ? l.labels.join(', ')
          : (l.values ?? []).map(String).join(', ');
      }
    );
    return {
      sitrepNameById: toMap(sitrepOptions),
      neoNameById: toMap(neoOptions),
      incidentNameById: toMap(incidentOptions),
      exerciseNameById: toMap(exerciseOptions),
      layerTimeLabelById,
      layerLegendLabelById,
    };
  }, [
    sitrepOptions,
    neoOptions,
    incidentOptions,
    exerciseOptions,
    mapCtx?.externalLayerTimeFilterById,
    mapCtx?.legendClassFilterByLayerId,
  ]);

  // --- auto-sync effects (same as legacy panel) ---
  useEffect(() => {
    if (usingSitrep || !currentSitrepId || sitrepOptions.length === 0) return;
    setSelections((prev) => syncSitrepSelection(prev, currentSitrepId));
  }, [currentSitrepId, sitrepOptions.length, usingSitrep, setSelections]);

  useEffect(() => {
    if (!currentIncident?.id || incidentOptions.length === 0) return;
    setSelections((prev) =>
      syncWorkspaceSelection(prev, 'Incident', currentIncident.id)
    );
  }, [currentIncident?.id, incidentOptions.length, setSelections]);

  useEffect(() => {
    if (!currentExercise?.id || exerciseOptions.length === 0) return;
    setSelections((prev) =>
      syncWorkspaceSelection(prev, 'Exercise', currentExercise.id)
    );
  }, [currentExercise?.id, exerciseOptions.length, setSelections]);

  const configCtx = useMemo(
    () => ({
      usingSitrep,
      currentIncidentId: currentIncident?.id,
      currentExerciseId: currentExercise?.id,
    }),
    [usingSitrep, currentIncident?.id, currentExercise?.id]
  );

  const isSelected = useCallback(
    (source: string) => isSourceSelected(safeSelections, source),
    [safeSelections]
  );

  const selectedSources = useMemo<DataSourceOption[]>(
    () =>
      safeSelections.map((sel) => {
        const base = allSources.find((s) => s.source === sel.source);
        return {
          id: sel.id ?? '',
          description: sel.description,
          isCustom: !!sel.customData,
          ...base,
          source: sel.source,
          customData: (sel.customData ?? base?.customData) as
            | DataSourceOption['customData']
            | undefined,
        };
      }),
    [safeSelections, allSources]
  );

  const toggle = useCallback(
    (source: DataSourceOption): ConfigTab => {
      const { next, openTab } = toggleSource(safeSelections, source, configCtx);
      setSelections(next);
      return openTab;
    },
    [safeSelections, configCtx, setSelections]
  );

  return {
    allSources,
    standardSources: filtered.filter((s) => !s.isCustom),
    customSources: filtered.filter((s) => s.isCustom),
    selectedSources,
    isSelected,
    needsConfig: (source) =>
      needsConfiguration(safeSelections, source, configCtx),
    displayName: (source) => displayNameFor(safeSelections, source, lookups),
    count: selectionDisplayCount(safeSelections),
    toggle,
    remove: (source) =>
      setSelections((prev) => prev.filter((s) => s.source !== source)),
    setId: (source, id) =>
      setSelections((prev) => setSourceId(prev, source, id)),
    fileIds: getSelectedFileIds(safeSelections),
    setFileIds: (ids) => setSelections((prev) => setSelectedFiles(prev, ids)),
    layers: getSelectedLayers(safeSelections),
    setLayers: (layers) =>
      setSelections((prev) => setSelectedLayers(prev, layers)),
    layersFilterByDatetime: getLayersFilterByDatetime(safeSelections),
    setLayersFilterByDatetimeEnabled: (enabled) =>
      setSelections((prev) => setLayersFilterByDatetime(prev, enabled)),
    seeristRequest: getSeeristRequest(safeSelections),
    setSeerist: (request) =>
      setSelections((prev) => setSeeristRequest(prev, request)),
    webDateRange: getWebDateRange(safeSelections),
    setWebDateRange: (range) =>
      setSelections((prev) => setWebDateRange(prev, range)),
    sitrepOptions,
    neoOptions,
    incidentOptions,
    exerciseOptions,
    workspaceId: currentIncident?.id || currentExercise?.id || group.group_guid,
  };
};
